import {useState, useEffect} from "react";
import {useNavigate} from "react-router-dom";
import {Form, Button, Table, Alert, Spinner} from "react-bootstrap";
import axios from "./axiosConfig";
import './UserPanel.css';

interface Tenant {
    id: number;
    name: string;
}

const TenantForm = () => {
    const navigate = useNavigate();
    const [tenantName, setTenantName] = useState("");
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [tenants, setTenants] = useState<Tenant[]>([]);

    const fetchTenants = async () => {
        try {
            const response = await axios.get("/api/tenants");
            setTenants(response.data as Tenant[]);
        } catch (err) {
            setError("Failed to fetch tenants.");
        }
    };

    useEffect(() => {
        fetchTenants();
    }, []);

    const validateTenantName = (name: string) => {
        // 验证是否是以三个字母开头，总共八位长，且至少包含一个数字的字符串
        return /^[A-Za-z]{3}[A-Za-z0-9]{5}$/.test(name) && /[0-9]/.test(name);
    };

    const handleCreateTenant = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!tenantName || !email) {
            setError("All fields are required.");
            return;
        }

        if (!validateTenantName(tenantName)) {
            setError("Tenant name must be exactly 8 characters long, start with three letters, and include at least one number.");
            return;
        }

        setLoading(true);
        try {
            // 验证租户名称是否唯一
            const nameCheck = await axios.get(`/api/tenants/check-name`, {params: {name: tenantName}});
            if (!nameCheck.data.isAvailable) {
                setError("Tenant name is already taken. Please choose another name.");
                setLoading(false);
                return;
            }

            const response = await axios.post("/api/tenants", {
                name: tenantName,
                admin_email: email,
            });
            fetchTenants();
            navigate(`/tenants/${response.data.id}/edit`);
        } catch (err) {
            setError("Failed to create tenant.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container mt-4">
            {error && <Alert variant="danger" dismissible onClose={() => setError("")}>{error}</Alert>}

            <Form onSubmit={handleCreateTenant}>
                <Form.Group className="mb-3">
                    <Form.Label>Tenant ID</Form.Label>
                    <Form.Control
                        type="text"
                        value={tenantName}
                        onChange={(e) => setTenantName(e.target.value)}
                        required
                    />
                </Form.Group>
                <Form.Group className="mb-3">
                    <Form.Label>Admin Email</Form.Label>
                    <Form.Control
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                    />
                </Form.Group>

                <Button type="submit" variant="primary" disabled={loading}>
                    {loading ? <Spinner animation="border" size="sm"/> : "Create Tenant"}
                </Button>
            </Form>

            <h3 className="mt-4">Existing Tenants</h3>
            <Table striped bordered hover size="sm" responsive variant="dark">
                <thead>
                <tr>
                    <th>Tenant ID</th>
                    <th>Tenant Name</th>
                </tr>
                </thead>
                <tbody>
                {tenants.map((tenant, index) => (
                    <tr key={index}>
                        <td>{index + 1}</td>
                        <td>{tenant.name}</td>
                    </tr>
                ))}
                </tbody>
            </Table>
        </div>
    );
};

export default TenantForm;
